'use client';
/**
 * LES PROFILS DE MATÉRIEL — matière × état d'arrivée (RQ, 07/09/2026).
 *
 * « Un sérum congelé se transporte de la même façon quel que soit l'examen » :
 * les conditions de transport, de réception et de refus vivent ICI, une fois
 * par matière et par état, et les examens s'y RÉFÈRENT (`fiche.materiels`).
 * L'écran est une grille : une ligne par matière, une colonne par état ; une
 * case = un profil, avec sa pastille d'endossement. Dessous, le REGISTRE DES
 * POINTS D'ENTRÉE liste tout à plat pour endosser en lot ce que la littérature
 * a posé (`backend/src/data/profils-materiel.ts`, `valide: false`).
 *
 * Ce qui ne se change pas : le code, la matière, l'état — un profil renommé
 * est un autre profil, et les examens qui le citent le perdraient sans bruit.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { card, EmptyState, Skeleton, Modale, inputStyle } from '@/components/ui';
import { moteur, collection } from './Registres';

import { Bouton } from '@/shared/Bouton';
import { useGed, userName } from '@/lib/store';
import { styleBouton } from '@/shared/boutonStyles';
import { fmtDate } from '@/lib/meta';
import { enTeteCollant, Tableau, ligneOuvrable } from '@/shared/Tableau';
type Critere = { libelle: string; requis: boolean };
export type Profil = {
  documentId: string; code: string; libelle: string; matiere: string; etat: string; recipient: string | null;
  transportMode: 'ambiant' | 'refrigere' | 'congele'; transportDelaiMax: string | null; transportConsigne: string | null;
  resteAFaire: string[]; criteresReception: Critere[]; attestations: string[]; donneesReception: string[]; refuses: string[];
  source: string | null; valide: boolean; validePar: string | null; valideLe: string | null; actif: boolean; ordre: number | null;
  version?: number | null;
};
type Traitement = { code: string; nom: string };

export const ETATS: { code: string; libelle: string; court: string }[] = [
  { code: 'tube_primaire', libelle: 'Tube / récipient primaire', court: 'Primaire' },
  { code: 'matiere_separee', libelle: 'Matière séparée (sérum, plasma décanté)', court: 'Séparée' },
  { code: 'matiere_congelee', libelle: 'Matière congelée', court: 'Congelée' },
  { code: 'isolat', libelle: 'Isolat (culture pure)', court: 'Isolat' },
  { code: 'adn_extrait', libelle: 'ADN extrait', court: 'ADN' },
  { code: 'tissu', libelle: 'Tissu (bloc, lames)', court: 'Tissu' },
];
export const MODES: Record<string, { libelle: string; couleur: string }> = {
  ambiant: { libelle: 'Ambiant', couleur: 'var(--amber-600)' },
  refrigere: { libelle: 'Réfrigéré 2–8 °C', couleur: 'var(--blue-600)' },
  congele: { libelle: 'Congelé', couleur: 'var(--violet-600)' },
};

const CHAMPS = ['code', 'libelle', 'matiere', 'etat', 'recipient', 'transportMode', 'transportDelaiMax', 'transportConsigne',
  'resteAFaire', 'criteresReception', 'attestations', 'donneesReception', 'refuses', 'source', 'valide', 'validePar', 'valideLe', 'actif', 'ordre',
  // La version relue part avec l'endossement : le moteur n'endosse que celle-là (14/10/2026).
  'version'];

function lireProfil(r: Record<string, unknown>): Profil {
  const liste = (v: unknown) => (Array.isArray(v) ? v.map(String) : []);
  return {
    documentId: String(r.documentId), code: String(r.code), libelle: String(r.libelle || ''), matiere: String(r.matiere || ''),
    etat: String(r.etat || ''), recipient: (r.recipient as string) || null,
    transportMode: (r.transportMode as Profil['transportMode']) || 'ambiant',
    transportDelaiMax: (r.transportDelaiMax as string) || null, transportConsigne: (r.transportConsigne as string) || null,
    resteAFaire: liste(r.resteAFaire),
    criteresReception: Array.isArray(r.criteresReception) ? (r.criteresReception as Critere[]).map((c) => ({ libelle: String(c.libelle || ''), requis: c.requis !== false })) : [],
    attestations: liste(r.attestations), donneesReception: liste(r.donneesReception), refuses: liste(r.refuses),
    source: (r.source as string) || null, valide: !!r.valide, validePar: (r.validePar as string) || null, valideLe: (r.valideLe as string) || null,
    actif: r.actif !== false, ordre: typeof r.ordre === 'number' ? r.ordre : null,
    version: typeof r.version === 'number' ? r.version : null,
  };
}

const pastille = (valide: boolean): React.CSSProperties => ({
  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
  background: valide ? 'var(--green-600)' : 'var(--amber-500)',
});

export function OngletProfilsMateriel({ estRq }: { estRq: boolean }) {
  const { settings } = useGed();
  const [profils, setProfils] = useState<Profil[] | null>(null);
  const [traitements, setTraitements] = useState<Traitement[]>([]);
  const [version, setVersion] = useState(0);
  const [ouvert, setOuvert] = useState<Profil | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let vivant = true;
    void Promise.all([
      collection('profil-materiels', CHAMPS, 'ordre:asc'),
      collection('techniques', ['code', 'nom', 'nature'], 'ordre:asc'),
    ]).then(([p, t]) => {
      if (!vivant) return;
      setProfils(p.map(lireProfil));
      setTraitements(t.filter((x) => x.nature === 'preanalytique').map((x) => ({ code: String(x.code), nom: String(x.nom) })));
    });
    return () => { vivant = false; };
  }, [version]);

  const matieres = useMemo(() => {
    const vues: string[] = [];
    for (const p of profils || []) if (!vues.includes(p.matiere)) vues.push(p.matiere);
    return vues;
  }, [profils]);
  const cellule = (matiere: string, etat: string) => (profils || []).filter((p) => p.matiere === matiere && p.etat === etat);

  if (!profils) return <Skeleton rows={6} />;
  if (!profils.length) return <EmptyState titre="Aucun profil de matériel" indice="Les profils se posent au démarrage du moteur depuis la littérature ; s'ils manquent, le moteur n'a pas encore redémarré avec le modèle du 07/09/2026." />;

  const aEndosser = profils.filter((p) => !p.valide);

  return (
    <div>
      <div style={{ ...card, padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>Profils de matériel — matière × état d’arrivée</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
          Une case = les conditions de transport, de réception et de refus d’une matière dans un état, valables pour
          tous les examens qui s’y réfèrent. <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={pastille(true)} /> endossé par la RQ</span>
          {' · '}<span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={pastille(false)} /> posé depuis la littérature, à relire</span>.
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table ref={enTeteCollant} className="ged-tableau" style={{ borderCollapse: 'collapse', fontSize: 12.5, minWidth: 760 }}>
            <thead>
              <tr>
                <th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', color: 'var(--text-muted)', fontSize: 'var(--tableau-entete-police)', textTransform: 'uppercase' }}>Matière</th>
                {ETATS.map((e) => <th scope="col" key={e.code} title={e.libelle} style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', color: 'var(--text-muted)', fontSize: 'var(--tableau-entete-police)', textTransform: 'uppercase' }}>{e.court}</th>)}
              </tr>
            </thead>
            <tbody>
              {matieres.map((m) => (
                <tr key={m} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontWeight: 600, whiteSpace: 'nowrap' }}>{m}</td>
                  {ETATS.map((e) => (
                    <td key={e.code} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', verticalAlign: 'top' }}>
                      {cellule(m, e.code).map((p) => (
                        <button key={p.documentId} type="button" className="ged-btn" onClick={() => setOuvert(p)}
                          title={`${p.libelle}\n${MODES[p.transportMode]?.libelle} · ${p.transportDelaiMax || '—'}`}
                          style={{ ...styleBouton('contour', 'sm'), display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, borderLeft: `3px solid ${MODES[p.transportMode]?.couleur || 'var(--border-default)'}`, fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>
                          <span style={pastille(p.valide)} />{p.code}
                        </button>
                      ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ ...card, padding: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>
          Registre des points d’entrée — {profils.length} profil(s), {aEndosser.length} à relire
        </div>
        {message && <div style={{ fontSize: 12.5, color: 'var(--green-600)', marginBottom: 6 }}>{message}</div>}
        {/* L10 (11/10/2026) : registre AVANCÉ sur le socle — l'endossement RQ est
            une action PAR LOT du socle (cases, confirmation nommée, un appel par
            profil, bilan). La ligne ouvre l'éditeur. */}
        <Tableau
          id="profils-materiel"
          ajusterLargeur
          niveau="avance"
          lignes={profils}
          cleLigne={(p) => p.documentId}
          ligneProps={(p) => ligneOuvrable(() => setOuvert(p), `Ouvrir le profil ${p.code}`)}
          vide={{ titre: 'Aucun profil de matériel.' }}
          colonnes={[
            { cle: 'code', libelle: 'Code', valeur: (p) => p.code, epinglee: true, obligatoire: true, style: { fontFamily: 'var(--font-mono)' }, rendu: (p) => <strong>{p.code}</strong> },
            { cle: 'libelle', libelle: 'Profil', valeur: (p) => p.libelle, rendu: (p) => p.libelle },
            {
              cle: 'transportMode', libelle: 'Transport', type: 'liste', valeur: (p) => p.transportMode, libelleValeur: (v) => MODES[v]?.libelle || v,
              rendu: (p) => <span style={{ color: MODES[p.transportMode]?.couleur, fontWeight: 600 }}>{MODES[p.transportMode]?.libelle}</span>,
            },
            { cle: 'transportDelaiMax', libelle: 'Délai', valeur: (p) => p.transportDelaiMax || '', rendu: (p) => p.transportDelaiMax || '—' },
            { cle: 'resteAFaire', libelle: 'Reste à faire', valeur: (p) => p.resteAFaire.join(' → '), style: { fontFamily: 'var(--font-mono)' }, rendu: (p) => p.resteAFaire.join(' → ') || '—' },
            {
              cle: 'valide', libelle: 'Endossé', type: 'liste', valeur: (p) => (p.valide ? 'endosse' : 'a_relire'),
              libelleValeur: (v) => (v === 'endosse' ? 'endossé par la RQ' : 'à relire'),
              rendu: (p) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={pastille(p.valide)} />{p.valide ? `${p.validePar ? userName(settings, p.validePar) : 'RQ'}${p.valideLe ? ' · ' + fmtDate(p.valideLe) : ''}` : 'à relire'}
                </span>
              ),
            },
          ]}
          actionsLot={estRq ? [
            {
              cle: 'endosser', libelle: 'Endosser', nature: 'autre', quoi: 'profils', applicable: (p) => !p.valide,
              executer: async (p) => {
                /* `versions` (14/10/2026) : un profil corrigé depuis la lecture
                   n'est pas endossé — le moteur le rend dans `perimes`. */
                const r = await moteur('manuel-ref/profils-materiel/validation', {
                  documentIds: [p.documentId], valide: true,
                  ...(typeof p.version === 'number' ? { versions: { [p.documentId]: p.version } } : {}),
                });
                if (!r.ok) throw new Error(r.message);
                if (Array.isArray(r.data.perimes) && r.data.perimes.length) {
                  throw new Error(String(r.data.message || 'Profil corrigé depuis votre lecture : rechargez et relisez.'));
                }
              },
              apresLot: (b) => { setMessage(b.echecs.length ? '' : `${b.reussies} profil(s) endossé(s).`); setVersion((v) => v + 1); },
            },
            {
              cle: 'relire', libelle: 'Remettre en relecture', nature: 'autre', quoi: 'profils', applicable: (p) => p.valide,
              executer: async (p) => {
                const r = await moteur('manuel-ref/profils-materiel/validation', { documentIds: [p.documentId], valide: false });
                if (!r.ok) throw new Error(r.message);
              },
              apresLot: (b) => { setMessage(b.echecs.length ? '' : `${b.reussies} profil(s) remis en relecture.`); setVersion((v) => v + 1); },
            },
          ] : undefined}
        />
      </div>

      {ouvert && (
        <EditeurProfil profil={ouvert} traitements={traitements} estRq={estRq}
          onClose={() => setOuvert(null)} onEcrit={() => { setOuvert(null); setVersion((v) => v + 1); }} />
      )}
    </div>
  );
}

/** Une ligne par élément ; les critères portent « (facultatif) » quand ils ne sont pas requis. */
const lignes = (l: string[]) => l.join('\n');
const deLignes = (t: string) => t.split('\n').map((x) => x.trim()).filter(Boolean);
const criteresEnTexte = (c: Critere[]) => c.map((x) => `${x.libelle}${x.requis ? '' : ' (facultatif)'}`).join('\n');
const criteresDeTexte = (t: string): Critere[] => deLignes(t).map((l) => {
  const facultatif = /\(facultatif\)\s*$/i.test(l);
  return { libelle: l.replace(/\s*\(facultatif\)\s*$/i, ''), requis: !facultatif };
});

function EditeurProfil({ profil, traitements, estRq, onClose, onEcrit }: {
  profil: Profil; traitements: Traitement[]; estRq: boolean; onClose: () => void; onEcrit: () => void;
}) {
  const { settings } = useGed();
  const [f, setF] = useState({
    libelle: profil.libelle, transportMode: profil.transportMode, transportDelaiMax: profil.transportDelaiMax || '',
    transportConsigne: profil.transportConsigne || '', resteAFaire: profil.resteAFaire,
    criteres: criteresEnTexte(profil.criteresReception), attestations: lignes(profil.attestations),
    donnees: lignes(profil.donneesReception), refuses: lignes(profil.refuses), source: profil.source || '',
  });
  const [envoi, setEnvoi] = useState(false); const [erreur, setErreur] = useState('');
  const zone: React.CSSProperties = { ...inputStyle, width: '100%', fontSize: 12.5, minHeight: 64, fontFamily: 'inherit' };
  const etiquette: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', margin: '10px 0 4px' };

  const enregistrer = (valide?: boolean) => {
    if (envoi) return;
    setEnvoi(true); setErreur('');
    const corps: Record<string, unknown> = {
      libelle: f.libelle, transportMode: f.transportMode, transportDelaiMax: f.transportDelaiMax, transportConsigne: f.transportConsigne,
      resteAFaire: f.resteAFaire, criteresReception: criteresDeTexte(f.criteres), attestations: deLignes(f.attestations),
      donneesReception: deLignes(f.donnees), refuses: deLignes(f.refuses), source: f.source,
      ...(valide === undefined ? {} : { valide }),
    };
    void moteur(`manuel-ref/profils-materiel/${profil.documentId}`, corps).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      onEcrit();
    });
  };

  const etat = ETATS.find((e) => e.code === profil.etat);
  return (
    <Modale titre={`${profil.code} — ${profil.libelle}`} onClose={onClose} largeur={680}
      sousTitre={<>{profil.matiere} · {etat?.libelle || profil.etat}{profil.recipient ? <> · récipient <code>{profil.recipient}</code></> : null}
        {' · '}{profil.valide ? <span style={{ color: 'var(--green-600)' }}>endossé par {profil.validePar ? userName(settings, profil.validePar) : 'la RQ'}{profil.valideLe ? ` le ${fmtDate(profil.valideLe)}` : ''}</span> : <span style={{ color: 'var(--amber-600)' }}>posé depuis la littérature — à relire</span>}</>}
      sansSaisie={!estRq}
      pied={estRq ? (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {erreur && <span style={{ fontSize: 12.5, color: 'var(--red-600)', alignSelf: 'center' }}>{erreur}</span>}
          {profil.valide && <Bouton taille="sm" type="button" disabled={envoi} onClick={() => enregistrer(false)}>Retirer l’endossement</Bouton>}
          <Bouton taille="sm" type="button" disabled={envoi} onClick={() => enregistrer()}>Enregistrer (à relire)</Bouton>
          <Bouton variante="primaire" taille="sm" type="button" disabled={envoi} onClick={() => enregistrer(true)}>Enregistrer et endosser</Bouton>
        </div>
      ) : undefined}>
      <div style={{ fontSize: 12.5 }}>
        <div style={etiquette}>Libellé</div>
        <input value={f.libelle} disabled={!estRq} onChange={(e) => setF({ ...f, libelle: e.currentTarget.value })} style={{ ...inputStyle, width: '100%', fontSize: 12.5 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <div style={etiquette}>Transport</div>
            <select value={f.transportMode} disabled={!estRq} onChange={(e) => setF({ ...f, transportMode: e.currentTarget.value as Profil['transportMode'] })} style={{ ...inputStyle, width: '100%', fontSize: 12.5 }}>
              {Object.entries(MODES).map(([c, m]) => <option key={c} value={c}>{m.libelle}</option>)}
            </select>
          </div>
          <div>
            <div style={etiquette}>Délai maximal</div>
            <input value={f.transportDelaiMax} disabled={!estRq} onChange={(e) => setF({ ...f, transportDelaiMax: e.currentTarget.value })} style={{ ...inputStyle, width: '100%', fontSize: 12.5 }} />
          </div>
        </div>
        <div style={etiquette}>Consigne de transport</div>
        <textarea value={f.transportConsigne} disabled={!estRq} onChange={(e) => setF({ ...f, transportConsigne: e.currentTarget.value })} style={zone} />

        <div style={etiquette}>Reste à faire à réception (traitements pré-analytiques, dans l’ordre)</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {traitements.map((t) => {
            const pos = f.resteAFaire.indexOf(t.code);
            return (
              <Bouton variante={pos >= 0 ? 'primaire' : 'contour'} taille="sm" key={t.code} type="button" disabled={!estRq} title={t.nom} onClick={() => setF({ ...f, resteAFaire: pos >= 0 ? f.resteAFaire.filter((c) => c !== t.code) : [...f.resteAFaire, t.code] })}>
                {pos >= 0 ? `${pos + 1}. ` : ''}{t.nom}
              </Bouton>
            );
          })}
          {!traitements.length && <span style={{ color: 'var(--text-muted)' }}>Aucun traitement pré-analytique déclaré (techniques de nature « preanalytique »).</span>}
        </div>

        <div style={etiquette}>Critères de réception — une ligne par critère, « (facultatif) » en fin de ligne s’il n’est pas requis</div>
        <textarea value={f.criteres} disabled={!estRq} onChange={(e) => setF({ ...f, criteres: e.currentTarget.value })} style={{ ...zone, minHeight: 90 }} />
        <div style={etiquette}>Attestations demandées à l’envoyeur (matériel préparé ailleurs)</div>
        <textarea value={f.attestations} disabled={!estRq} onChange={(e) => setF({ ...f, attestations: e.currentTarget.value })} style={zone} />
        <div style={etiquette}>Données relevées à réception</div>
        <textarea value={f.donnees} disabled={!estRq} onChange={(e) => setF({ ...f, donnees: e.currentTarget.value })} style={zone} />
        <div style={etiquette}>Motifs de refus</div>
        <textarea value={f.refuses} disabled={!estRq} onChange={(e) => setF({ ...f, refuses: e.currentTarget.value })} style={zone} />
        <div style={etiquette}>Source</div>
        <input value={f.source} disabled={!estRq} onChange={(e) => setF({ ...f, source: e.currentTarget.value })} style={{ ...inputStyle, width: '100%', fontSize: 12.5 }} />
      </div>
    </Modale>
  );
}
